/**
 * ServiceNow can put whole sections inside one list item. Lexical moves every
 * nested list to the end of that item on import, separating steps from headings.
 * Split the item into runs before importing so each nested list stays in place.
 * A list-only item is Lexical's canonical representation of a nested list.
 */
export const preserveHtmlListOrder = (document: Document): void => {
  // Handle inner items first so moving an outer run does not skip descendants.
  const items = Array.from(document.querySelectorAll('li')).reverse();
  for (const item of items) {
    const children = Array.from(item.childNodes);
    const isList = (node: ChildNode) => node.nodeName === 'UL' || node.nodeName === 'OL';
    const firstList = children.findIndex(isList);
    const hasContentAfterList = children
      .slice(firstList + 1)
      .some((child) => isList(child) || child.nodeType !== 3 || Boolean(child.textContent?.trim()));
    if (firstList === -1 || !hasContentAfterList) {
      continue;
    }

    const fragment = document.createDocumentFragment();
    let run: HTMLLIElement | null = null;
    for (const child of children) {
      if (isList(child)) {
        const wrapper = document.createElement('li');
        wrapper.append(child);
        fragment.append(wrapper);
        run = null;
      } else {
        // Formatting whitespace between blocks must not create empty bullets.
        if (!run && child.nodeType === 3 && !child.textContent?.trim()) {
          continue;
        }
        if (!run) {
          run = item.cloneNode(false) as HTMLLIElement;
          // Only the original item can own an explicit ordered-list value/id.
          if (fragment.childNodes.length) {
            run.removeAttribute('value');
            run.removeAttribute('id');
          }
          fragment.append(run);
        }
        run.append(child);
      }
    }
    item.replaceWith(fragment);
  }
};

export const normalizeHtmlListOrder = (content: string): string => {
  if (!/<(?:ul|ol)\b/i.test(content)) {
    return content;
  }
  const document = new DOMParser().parseFromString(content, 'text/html');
  preserveHtmlListOrder(document);
  return document.body.innerHTML;
};
